import React, {useEffect, useState} from 'react';
import {
  AccordionDetails,
  AccordionSummary,
  Accordion as MuiAccordion,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrCheckbox from '../arr-checkbox/ArrCheckbox';
import './ArrAccordionWithCheckbox.scss';

export interface AccordionSectionWithCheckbox {
  id: string;
  title: {
    primaryText: string | React.ReactElement;
    secondaryText?: string | React.ReactElement;
  };
  content: string | React.ReactElement;
  isDisabled?: boolean;
  checked?: boolean;
  showCheckbox?: boolean | true;
}

export interface AccordionWithCheckboxProps {
  sections: AccordionSectionWithCheckbox[];
  expandedSection?: string;
  onToggle?: (sectionId: string) => void;
  onCheckChange?: (sectionId: string, checked: boolean) => void;
  allowMultiple?: boolean;
  disableCheckboxEdit?: boolean;
  multipleExtendedSections?: string[] | null;
}

const ArrAccordionWithCheckbox: React.FC<AccordionWithCheckboxProps> = ({
  sections,
  expandedSection,
  onToggle,
  onCheckChange,
  allowMultiple = false,
  disableCheckboxEdit = false,
  multipleExtendedSections,
}) => {
  const [expanded, setExpanded] = useState<string[]>(
    expandedSection ? [expandedSection] : []
  );

  useEffect(() => {
    if (expandedSection || multipleExtendedSections) {
      setExpanded(
        expandedSection ? [expandedSection] : multipleExtendedSections || []
      );
    }
  }, [expandedSection, multipleExtendedSections]);

  const handleToggle = (sectionId: string) => {
    if (allowMultiple) {
      setExpanded((prev) =>
        prev.includes(sectionId)
          ? prev.filter((id) => id !== sectionId)
          : [...prev, sectionId]
      );
    } else {
      setExpanded((prev) => (prev.includes(sectionId) ? [] : [sectionId]));
    }
    onToggle?.(sectionId);
  };

  const handleKeyDown = (e: React.KeyboardEvent, sectionId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(sectionId);
    }
  };

  return (
    <div
      className="custom-accordion-with-checkbox"
      data-testid={`arr-accordion-with-checkbox-wrapper`}
    >
      {sections.map((section, index) => {
        const isExpanded = expanded.includes(section.id);
        const {showCheckbox = true} = section;
        return (
          <MuiAccordion
            key={section.id}
            expanded={isExpanded}
            disabled={section.isDisabled}
            className={`accordion-section ${section.isDisabled ? 'disabled' : ''}`}
            data-testid={`arr-accordion-${section.id}`}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              onClick={() => !section.isDisabled && handleToggle(section.id)}
              onKeyDown={(e) => handleKeyDown(e, section.id)}
              tabIndex={section.isDisabled ? -1 : 0}
              role="button"
              data-testid={`arr-summary-${section.id}`}
            >
              <div
                className="accordion-header-content"
                data-testid={`arr-accordion-with-checkbox-div-cb-wrapper-${index}`}
              >
                {showCheckbox && (
                  <ArrCheckbox
                    checked={!!section.checked}
                    disabled={section.isDisabled || disableCheckboxEdit}
                    onChange={(checked) => {
                      if (!disableCheckboxEdit) {
                        onCheckChange?.(section.id, checked);
                      }
                    }}
                    aria-label={`arr-accordion-checkbox-${section.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (disableCheckboxEdit) e.preventDefault();
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (disableCheckboxEdit) return;
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        onCheckChange?.(section.id, !section.checked);
                      }
                    }}
                    data-testid={`${section.id}-arr-accordion-with-checkbox`}
                  />
                )}
                <div
                  className="accordion-title-primary"
                  data-testid={`arr-title-primary-${section.id}`}
                >
                  {section.title.primaryText}
                </div>
                <div
                  className="accordion-title-secondary"
                  data-testid={`arr-accordion-with-checkbox-div-secondary-text-${index}`}
                >
                  {section.title.secondaryText}
                </div>
              </div>
            </AccordionSummary>
            <AccordionDetails
              data-testid={`arr-details-${section.id}`}
              className={`accordion-details ${isExpanded ? 'expanded' : ''}`}
            >
              {section.content}
            </AccordionDetails>
          </MuiAccordion>
        );
      })}
    </div>
  );
};

export default ArrAccordionWithCheckbox;
